const HERO_IMAGE =
    "https://customer-assets.emergentagent.com/job_a4c9c179-d042-4742-b732-bd5e37197666/artifacts/sw53cej4_Image%206.jpeg";

/* ───────────────────────────────────────────────
   Instagram 4:5 portrait layouts (1080 × 1350)
   Two variants: "teaser" (short copy) & "full"
   These render in a fixed 1080×1350 frame so they
   capture cleanly when recorded at that viewport.
   ─────────────────────────────────────────────── */

const Frame = ({ children }) => (
    <div className="ig-stage flex min-h-screen w-full items-center justify-center bg-black">
        <div
            data-testid="ig-frame"
            className="ig-frame relative overflow-hidden bg-[#050C08] text-[#F7F9F5]"
            style={{
                width: "1080px",
                height: "1350px",
                aspectRatio: "1080 / 1350",
            }}
        >
            {children}
        </div>
    </div>
);

const SharedBackdrop = () => (
    <>
        <div className="absolute inset-0 z-0">
            <img
                src={HERO_IMAGE}
                alt=""
                className="kenburns h-full w-full object-cover"
                loading="eager"
            />
        </div>
        {/* Localized warm sun-bloom behind the wordmark to lighten the sky there */}
        <div
            aria-hidden="true"
            className="absolute -top-[10%] -left-[10%] z-[5] h-[55%] w-[65%] rounded-full"
            style={{
                background:
                    "radial-gradient(circle at 30% 30%, rgba(255, 220, 170, 0.55), rgba(255, 200, 140, 0.28) 35%, rgba(255, 180, 110, 0.10) 60%, transparent 75%)",
                filter: "blur(40px)",
                mixBlendMode: "screen",
            }}
        />
        {/* Strong bottom-up dark gradient so copy area reads cleanly */}
        <div
            aria-hidden="true"
            className="absolute inset-0 z-10 bg-gradient-to-b from-[#050C08]/55 via-[#050C08]/35 to-[#050C08]/95"
        />
        <div
            aria-hidden="true"
            className="absolute inset-0 z-10 bg-gradient-to-t from-[#050C08] via-[#050C08]/70 to-transparent"
            style={{ top: "55%" }}
        />
        <div aria-hidden="true" className="grain z-10" />
        <div aria-hidden="true" className="fog-layer z-10" />
        <div aria-hidden="true" className="fog-layer fog-layer-2 z-10" />
        <div
            aria-hidden="true"
            className="dusk-glow absolute top-[18%] right-[-15%] z-10 h-[60%] w-[80%] rounded-full"
        />
    </>
);

const IgHeader = () => (
    <header className="relative z-20 flex items-center px-12 pt-12">
        <img
            src="/bushel-wordmark-lime.png"
            alt="Bushel"
            className="rise h-16 w-auto drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)]"
        />
    </header>
);

const ContactPill = () => (
    <span className="cta-glow font-display inline-flex items-center gap-3 rounded-full border border-white/20 bg-white/10 px-9 py-4 text-base font-semibold tracking-[0.28em] uppercase text-[#F7F9F5] shadow-[0_8px_32px_rgba(0,0,0,0.35)] backdrop-blur-xl backdrop-saturate-150">
        <span className="pulse-dot inline-block h-2.5 w-2.5 rounded-full bg-[#89F337]" />
        Contact Us
    </span>
);

/* ───── Teaser (shorter copy) ───── */
export const IgTeaser = () => {
    return (
        <Frame>
            <SharedBackdrop />
            <IgHeader />

            <section className="relative z-20 flex h-[calc(100%-7rem)] flex-col justify-end px-12 pb-16">
                <p className="rise delay-1 font-body mb-7 text-sm font-medium tracking-[0.36em] uppercase text-[#89F337]">
                    Coming Soon 2026
                </p>

                <h1 className="font-display text-[5.4rem] leading-[1.02] font-extrabold tracking-tight text-[#F7F9F5]">
                    <span className="rise delay-2 block">
                        Grown{" "}
                        <em className="font-medium text-[#89F337] not-italic">
                            clean.
                        </em>
                    </span>
                    <span className="rise delay-3 block">Grown local.</span>
                    <span className="rise delay-4 block">
                        Year{" "}
                        <span className="relative inline-block">
                            round.
                            <span
                                aria-hidden="true"
                                className="absolute right-0 -bottom-1 left-0 h-[4px] origin-left scale-x-100 bg-[#89F337]/80"
                            />
                        </span>
                    </span>
                </h1>

                <div className="rise delay-5 mt-12 flex items-center justify-between">
                    <ContactPill />
                    <p className="font-body text-base tracking-[0.22em] uppercase text-[#E8EFE9]">
                        bushel.ca · @bushelcanada
                    </p>
                </div>
            </section>
        </Frame>
    );
};

/* ───── Full (matches website) ───── */
export const IgFull = () => {
    return (
        <Frame>
            <SharedBackdrop />
            <IgHeader />

            <section className="relative z-20 flex h-[calc(100%-7rem)] flex-col justify-end px-12 pb-14">
                <p className="rise delay-1 font-body mb-6 text-sm font-medium tracking-[0.36em] uppercase text-[#89F337]">
                    Coming Soon 2026
                </p>

                <h1 className="font-display text-[4.8rem] leading-[1.02] font-extrabold tracking-tight text-[#F7F9F5]">
                    <span className="rise delay-2 block">
                        Grown{" "}
                        <em className="font-medium text-[#89F337] not-italic">
                            clean.
                        </em>
                    </span>
                    <span className="rise delay-3 block">Grown local.</span>
                    <span className="rise delay-4 block">
                        Year{" "}
                        <span className="relative inline-block">
                            round.
                            <span
                                aria-hidden="true"
                                className="absolute right-0 -bottom-1 left-0 h-[4px] origin-left scale-x-100 bg-[#89F337]/80"
                            />
                        </span>
                    </span>
                </h1>

                <p className="rise delay-4 font-body mt-8 max-w-2xl text-xl leading-relaxed tracking-wide text-[#C9D3CC]">
                    Pesticide-free, herbicide-free, and fungicide-free,
                    nutrient-rich leafy greens grown year round in an advanced
                    indoor vertical farm, located in Strathroy, Ontario.
                </p>

                <div className="rise delay-4 mt-7 max-w-md border-l-2 border-[#89F337]/60 pl-5">
                    <p className="font-body text-[12px] font-medium tracking-[0.32em] uppercase text-[#A3B3A8]">
                        In Partnership With
                    </p>
                    <p
                        className="mt-2 text-[2.4rem] leading-tight font-medium text-[#F7F9F5]"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                        YesHealth Group
                    </p>
                    <p className="font-body mt-1 text-base tracking-wide text-[#A3B3A8]">
                        Global vertical farming technology leader
                    </p>
                </div>

                <div className="rise delay-5 mt-10 flex items-center justify-between">
                    <ContactPill />
                    <p className="font-body text-base tracking-[0.22em] uppercase text-[#E8EFE9]">
                        bushel.ca · @bushelcanada
                    </p>
                </div>
            </section>
        </Frame>
    );
};
